import { Show, DeleteButton, DateField } from "@refinedev/antd";
import { useShow, useUpdate } from "@refinedev/core";
import {
  Descriptions,
  Alert,
  Space,
  Tag,
  Button,
  Typography,
  Card,
  Empty,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  FlagOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import type { Entry, Modo } from "@/lib/types";
import { CategoryTag } from "@/components/CategoryTag";
import { formatDate } from "@/lib/format";

const { Text, Paragraph } = Typography;

const MODO_COLOR: Record<Modo, string> = {
  normal: "default",
  reuniao: "blue",
  desabafo: "magenta",
};
const MODO_LABEL: Record<Modo, string> = {
  normal: "Normal",
  reuniao: "Reunião",
  desabafo: "Desabafo",
};
const RECORR_LABEL: Record<string, string> = {
  diaria: "Diária",
  semanal: "Semanal",
  mensal: "Mensal",
};

/**
 * /admin/entries/:id — detalhe de moderação (ver / ocultar / excluir).
 *
 * Conteúdo de entradas privadas (`is_private`) fica oculto por padrão. O botão
 * "Revelar conteúdo" exibe localmente o texto SOMENTE durante a investigação —
 * na API real isso deve exigir autorização e registro em audit_log (LGPD §11).
 * "Ocultar" persiste `is_private=true` (redige) via useUpdate.
 */
export const EntryShow: React.FC = () => {
  const { queryResult } = useShow<Entry>();
  const { data, isLoading } = queryResult;
  const e = data?.data;
  const isPrivate = !!e?.is_private;

  const [revealed, setRevealed] = useState(false);
  const { mutate: update, isLoading: hiding } = useUpdate();

  const showText = !isPrivate || revealed;
  const extracted = e?.dados_extraidos ?? {};
  const hasExtracted = Object.keys(extracted).length > 0;

  return (
    <Show
      isLoading={isLoading}
      title="Entry · moderação"
      headerButtons={
        <Space>
          {!isPrivate && e?.id && (
            <Button
              icon={<EyeInvisibleOutlined />}
              loading={hiding}
              onClick={() =>
                update({
                  resource: "entries",
                  id: e.id,
                  values: { is_private: true },
                  successNotification: () => ({
                    type: "success",
                    message: "Entrada ocultada",
                    description: "O conteúdo foi redigido por moderação.",
                  }),
                })
              }
            >
              Ocultar conteúdo
            </Button>
          )}
          <DeleteButton recordItemId={e?.id} danger>
            Remover (moderação)
          </DeleteButton>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {isPrivate && (
          <Alert
            type="error"
            showIcon
            icon={<LockOutlined />}
            message="Entrada privada (modo Desabafo / saúde sensível)"
            description="Conteúdo oculto por padrão. Só revele com justificativa — na produção a ação é registrada em audit_log (LGPD §11)."
            action={
              <Button
                size="small"
                danger={!revealed}
                icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setRevealed((v) => !v)}
              >
                {revealed ? "Reocultar" : "Revelar conteúdo"}
              </Button>
            }
          />
        )}

        <Descriptions
          bordered
          column={{ xs: 1, sm: 2 }}
          size="small"
          title={
            <Space>
              <CategoryTag value={e?.categoria} />
              {e?.modo && (
                <Tag color={MODO_COLOR[e.modo] ?? "default"}>
                  {MODO_LABEL[e.modo] ?? e.modo}
                </Tag>
              )}
              {e?.flagged && (
                <Tag color="red" icon={<FlagOutlined />}>
                  denunciado
                </Tag>
              )}
              {isPrivate && (
                <Tag color="magenta" icon={<EyeInvisibleOutlined />}>
                  privado
                </Tag>
              )}
            </Space>
          }
        >
          <Descriptions.Item label="Texto" span={2}>
            {showText ? (
              <Paragraph style={{ marginBottom: 0 }}>
                {e?.texto_corrigido ?? e?.texto_original ?? "—"}
              </Paragraph>
            ) : (
              <Text type="secondary">
                <LockOutlined /> — conteúdo oculto —
              </Text>
            )}
          </Descriptions.Item>
          {showText && e?.texto_corrigido && e.texto_corrigido !== e.texto_original && (
            <Descriptions.Item label="Texto original" span={2}>
              <Text type="secondary">{e.texto_original}</Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Resumo" span={2}>
            {showText ? e?.resumo ?? "—" : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Data do evento">
            {e?.data_evento ? <DateField value={e.data_evento} format="DD/MM/YYYY HH:mm" /> : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Disparo (lembrete)">
            {e?.data_hora_disparo ? (
              <DateField value={e.data_hora_disparo} format="DD/MM/YYYY HH:mm" />
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Recorrência">
            {e?.recorrencia ? <Tag color="cyan">{RECORR_LABEL[e.recorrencia] ?? e.recorrencia}</Tag> : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Brain">
            <Text code>{e?.brain_id ?? "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Usuário (user_id)">
            <Text code>{e?.user_id ?? "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Criado em">
            {formatDate(e?.created_at)}
          </Descriptions.Item>
        </Descriptions>

        <Card
          className="mel-glass"
          bordered={false}
          size="small"
          title="Dados extraídos pela IA"
        >
          {hasExtracted && showText ? (
            <Descriptions column={1} size="small" bordered>
              {Object.entries(extracted).map(([k, v]) => (
                <Descriptions.Item key={k} label={k}>
                  {v == null || v === "" ? (
                    <Text type="secondary">—</Text>
                  ) : (
                    String(v)
                  )}
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                hasExtracted && !showText ? "Oculto (entrada privada)" : "Sem dados extraídos"
              }
            />
          )}
        </Card>
      </Space>
    </Show>
  );
};
